import { Routes } from '@angular/router';
import { dashboardChildGuard } from './shared/guards/dashboard.guard';
import { DocPrep } from './pages/auth/doc-prep/doc-prep';
import { KYCStatusGuard } from './shared/guards/kyc.guard';
import { permissionGuard } from './shared/guards/permission.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'auth/login',
    pathMatch: 'full',
  },
  {
    path: 'auth',
    loadComponent: () =>
      import('./core/layouts/auth-layout/auth-layout').then(
        (m) => m.AuthLayout,
      ),
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./pages/auth/login/login').then((m) => m.Login),
      },
      {
        path: 'check-email',
        loadComponent: () =>
          import('./pages/auth/check-email/check-email').then(
            (m) => m.CheckEmail,
          ),
      },
      {
        path: 'register',
        loadComponent: () =>
          import('./pages/auth/register/register').then((m) => m.Register),
      },
      {
        path: 'doc-prep',
        loadComponent: () =>
          import('./pages/auth/doc-prep/doc-prep').then((m) => m.DocPrep),
      },
      {
        path: 'account-type',
        loadComponent: () =>
          import('./pages/auth/account-type/account-type').then(
            (m) => m.AccountType,
          ),
        data: {
          showLogo: false,
          pageTitle: 'Let’s get to know your business',
          pageDescription: 'What type of business are you creating for',
        },
      },
      {
        path: 'otp',
        loadComponent: () =>
          import('./pages/auth/otp-verification/otp-verification').then(
            (m) => m.OtpVerification,
          ),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./pages/auth/forgot-password/forgot-password').then(
            (m) => m.ForgotPassword,
          ),
      },
      {
        path: 'new-password',
        loadComponent: () =>
          import('./pages/auth/new-password/new-password').then(
            (m) => m.NewPassword,
          ),
      },
    ],
  },
  {
    path: 'main',
    loadComponent: () =>
      import('./core/layouts/dashboard-layout/dashboard-layout').then(
        (m) => m.DashboardLayout,
      ),
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard/collections-dashboard/collections-dashboard').then(
            (m) => m.CollectionsDashboard,
          ),
        data: {
          parent: 'Dashboard',
          pageDescription: 'Welcome to your Dashboard',
        },
      },
      {
        path: 'product-list',
        loadComponent: () =>
          import('./pages/product/product-list/product-list').then(
            (m) => m.ProductList,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Products',
          permissions: ['merchant.products.view'],
        },
      },
      {
        path: 'add-new-product',
        loadComponent: () =>
          import('./pages/product/create-product/create-product').then(
            (m) => m.CreateProduct,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Add New Products',
          permissions: ['merchant.products.create'],
        },
      },
      {
        path: 'product-variant/:productId',
        loadComponent: () =>
          import('./pages/product/create-product-variant/create-product-variant').then(
            (m) => m.CreateProductVariant,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Add New Products',
          permissions: ['merchant.products.variants.create'],
        },
      },
      {
        path: 'product-edit/:productId',
        loadComponent: () =>
          import('./pages/product/create-product/create-product').then(
            (m) => m.CreateProduct,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Edit Products',
          permissions: ['merchant.products.update'],
        },
      },
      {
        path: 'product-variant-edit/:productId',
        loadComponent: () =>
          import('./pages/product/create-product-variant/create-product-variant').then(
            (m) => m.CreateProductVariant,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Edit Product Variants',
          permissions: ['merchant.products.variants.update'],
        },
      },
      {
        path: 'invoice',
        loadComponent: () =>
          import('./pages/invoice/invoice-list/invoice-list').then(
            (m) => m.InvoiceList,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Invoice',
          permissions: ['merchant.invoices.view'],
        },
      },
      {
        path: 'invoice-create',
        loadComponent: () =>
          import('./pages/invoice/create-invoice/create-invoice').then(
            (m) => m.CreateInvoice,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Create New Invoice',
          permissions: ['merchant.invoices.create'],
        },
      },
      {
        path: 'invoice-view/:id',
        loadComponent: () =>
          import('./pages/invoice/view-invoice/view-invoice').then(
            (m) => m.ViewInvoice,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'View Invoice',
          permissions: ['merchant.invoices.view_detail'],
        },
      },
      {
        path: 'payment-links',
        loadComponent: () =>
          import('./pages/payment-links/payment-links-list/payment-links-list').then(
            (m) => m.PaymentLinksList,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Payment Links',
          pageDescription:
            'Create reusable links to accept payments from anyone, anywhere.',
          permissions: ['merchant.payment_links.view'],
        },
      },
      {
        path: 'payment-links/new',
        loadComponent: () =>
          import('./pages/payment-links/payment-link-create/payment-link-create').then(
            (m) => m.PaymentLinkCreate,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'New Payment Link',
          permissions: ['merchant.payment_links.create'],
        },
      },
      {
        path: 'payment-links/:id/edit',
        loadComponent: () =>
          import('./pages/payment-links/payment-link-create/payment-link-create').then(
            (m) => m.PaymentLinkCreate,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Edit Payment Link',
          permissions: ['merchant.payment_links.update'],
        },
      },
      {
        path: 'payment-links/:id',
        loadComponent: () =>
          import('./pages/payment-links/payment-link-detail/payment-link-detail').then(
            (m) => m.PaymentLinkDetail,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Payment Link',
          permissions: ['merchant.payment_links.view'],
        },
      },
      {
        path: 'referrals',
        loadComponent: () =>
          import('./pages/referral/referral-list/referral-list').then(
            (m) => m.ReferralList,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Referrals',
          permissions: ['merchant.referrals.view'],
        },
      },
      {
        path: 'virtual-accounts',
        loadComponent: () =>
          import('./pages/virtual-accounts/virtual-accounts').then(
            (m) => m.VirtualAccounts,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Virtual Account',
          pageDescription:
            'Create and track dedicated virtual accounts for your customers.',
          permissions: ['merchant.virtual_accounts.view'],
        },
      },
      {
        path: 'virtual-accounts/report',
        loadComponent: () =>
          import('./pages/virtual-accounts/collections-report/va-collections-report').then(
            (m) => m.VaCollectionsReport,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Collection',
          permissions: ['merchant.virtual_accounts.reports.view'],
        },
      },
      {
        path: 'virtual-accounts/:id/history',
        loadComponent: () =>
          import('./pages/virtual-accounts/customer-history/customer-history').then(
            (m) => m.CustomerHistory,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'History',
          permissions: ['merchant.virtual_accounts.transactions.view'],
        },
      },
      {
        path: 'balance-hub',
        loadComponent: () =>
          import('./pages/balance-hub/balance-hub').then((m) => m.BalanceHub),
        canActivate: [permissionGuard],
        data: {
          parent: 'Balance Hub',
          permissions: ['merchant.balance_hub.view'],
        },
      },
      {
        path: 'wallet',
        loadComponent: () =>
          import('./pages/wallet/wallet').then((m) => m.WalletPage),
        canActivate: [permissionGuard],
        data: {
          parent: 'Wallet',
          permissions: ['merchant.wallet.view'],
        },
      },
      {
        path: 'wallet/sub-wallets',
        loadComponent: () =>
          import('./pages/wallet/sub-wallets/sub-wallets').then(
            (m) => m.SubWalletsPage,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Wallet',
          permissions: ['merchant.sub_wallet.view'],
        },
      },
      {
        path: 'wallet/sub-wallets/:id',
        loadComponent: () =>
          import('./pages/wallet/manage-wallet/manage-wallet').then(
            (m) => m.ManageWalletPage,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Manage Wallet',
          permissions: ['merchant.sub_wallet.view'],
        },
      },
      {
        path: 'settlement-report',
        loadComponent: () =>
          import('./pages/settlement-report/settlement-report').then(
            (m) => m.SettlementReport,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Reports',
          child: 'Settlement Report',
          permissions: ['merchant.settlement.view'],
        },
      },
      {
        path: 'chargeback-list',
        loadComponent: () =>
          import('./pages/chargeback/chargeback').then((m) => m.Chargeback),
        canActivate: [permissionGuard],
        data: {
          parent: 'Chargeback',
          child: '',
          permissions: ['merchant.chargeback.view'],
        },
      },
      {
        path: 'disbursement',
        loadChildren: () =>
          import('./pages/disbursement/disbursement.routes').then(
            (m) => m.routes,
          ),
        canActivate: [permissionGuard],
        data: {
          permissions: ['merchant.disbursement.view'],
        },
      },
      {
        path: 'sub-accounts',
        loadChildren: () =>
          import('./pages/sub-accounts/sub-accounts.routes').then(
            (m) => m.routes,
          ),
        canActivate: [permissionGuard],
        data: {
          permissions: ['merchant.sub_accounts.view'],
        },
      },
      {
        path: 'disbursement-setup',
        loadComponent: () =>
          import('./pages/disbursement/disbursement-setup/disbursement-setup').then(
            (m) => m.DisbursementSetup,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Disbursement Setup',
          permissions: ['merchant.disbursement.view'],
        },
      },
      {
        path: 'storefront',
        loadComponent: () =>
          import('./pages/store-front/store-front').then((m) => m.StoreFront),
        canActivate: [permissionGuard],
        data: {
          parent: 'Storefront',
          child: '',
          pageDescription:
            'This is how users would see your account in real time',
          permissions: ['merchant.storefront.view'],
        },
      },
      {
        path: 'storefront-templates',
        loadComponent: () =>
          import('./pages/storefront-customization/storefront-customization').then(
            (m) => m.StorefrontCustomization,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Customization',
          child: '',
          pageDescription:
            'This is how users would see your account in real time',
          permissions: ['merchant.storefront.template.update'],
        },
      },
      {
        path: 'storefront-customization',
        loadComponent: () =>
          import('./pages/storefront-customization/storefront-form/storefront-form').then(
            (m) => m.StorefrontForm,
          ),
        canActivate: [permissionGuard],
        data: {
          parent: 'Customize Storefront',
          permissions: ['merchant.storefront.branding.update'],
        },
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('./pages/settings/settings.routes').then((m) => m.routes),
        canActivate: [permissionGuard],
        data: {
          permissions: [
            'merchant.settings.charges.view',
            'merchant.settings.virtual_accounts.view',
            'merchant.settings.configuration_secret.view',
            'merchant.settings.collections.view',
            'merchant.settings.account_keys.view',
          ],
        },
      },
      {
        path: 'user-management',
        loadComponent: () =>
          import('./pages/user-management/user-management').then(
            (m) => m.UserManagement,
          ),
        data: {
          parent: 'User Management',
        },
      },
      {
        path: 'coming-soon',
        loadComponent: () => import('@shared').then((m) => m.ComingSoon),
        canActivate: [permissionGuard],
        data: { permissions: ['merchant.storefront.view'] },
      },
    ],
    canActivateChild: [dashboardChildGuard],
  },
  {
    path: 'kyc',
    loadComponent: () =>
      import('./core/layouts/kyc-layout/kyc-layout').then((m) => m.KycLayout),
    children: [
      {
        path: 'business-information',
        loadComponent: () =>
          import('./pages/kyc/business-information/business-information').then(
            (m) => m.BusinessInformation,
          ),
        canActivate: [permissionGuard],
        data: { permissions: ['merchant.kyc.business_info.view'] },
      },
      {
        path: 'business-verification',
        loadComponent: () =>
          import('./pages/kyc/business-verification/business-verification').then(
            (m) => m.BusinessVerification,
          ),
        canActivate: [permissionGuard],
        data: { permissions: ['merchant.kyc.business_verification.submit'] },
      },
      {
        path: 'document-upload',
        loadComponent: () =>
          import('./pages/kyc/document-upload/document-upload').then(
            (m) => m.DocumentUpload,
          ),
        canActivate: [permissionGuard],
        data: { permissions: ['merchant.kyc.documents.upload'] },
      },
      {
        path: 'bank-information',
        loadComponent: () =>
          import('./pages/kyc/bank-information/bank-information').then(
            (m) => m.BankInformation,
          ),
        canActivate: [permissionGuard],
        data: { permissions: ['merchant.kyc.bank_info.submit'] },
      },
      {
        path: 'business-details',
        loadComponent: () =>
          import('./pages/kyc/business-details/business-details').then(
            (m) => m.BusinessDetails,
          ),
        canActivate: [permissionGuard],
        data: { permissions: ['merchant.kyc.online_info.submit'] },
      },
      {
        path: 'review',
        loadComponent: () =>
          import('./pages/kyc/review/review').then((m) => m.Review),
        canActivate: [permissionGuard],
        data: { permissions: ['merchant.kyc.review.submit'] },
      },
    ],
    canActivateChild: [dashboardChildGuard],
  },
];
